import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Wordmark } from "./Brand";
import {
  LayoutDashboard, PenLine, FileText, CheckSquare, Building2, Users,
  Briefcase, BarChart3, Settings, LogOut, ShieldCheck, Lock, ClipboardList, Receipt, Home,
  School, UserCog,
} from "lucide-react";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isConsultant = !isAdmin || !!user?.is_consultant;

  const links = [
    { to: "/", label: "Hub Home", icon: Home, testid: "nav-hub-home-link", end: true },
    { to: "/ops", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", end: true },
    ...(isConsultant ? [{ to: "/my-schools", label: "My Schools", icon: School, testid: "nav-my-schools-link" }] : []),
    ...(isAdmin ? [{ to: "/consultants", label: "Consultants", icon: UserCog, testid: "nav-consultants-link" }] : []),
    { to: "/log-work", label: "Log Today's Work", icon: PenLine, testid: "nav-log-work-link" },
    { to: "/logs", label: "Daily Visit Logs", icon: FileText, testid: "nav-logs-link" },
    ...(isAdmin ? [{ to: "/review", label: "Review Queue", icon: CheckSquare, testid: "nav-review-queue-link" }] : []),
    ...(isAdmin ? [{ to: "/contracts", label: "Contracts & POs", icon: FileText, testid: "nav-contracts-link" }] : []),
    ...(isAdmin ? [{ to: "/client-invoices", label: "Client Invoices", icon: Receipt, testid: "nav-client-invoices-link" }] : []),
    { to: "/organizations", label: "Organizations", icon: Building2, testid: "nav-organizations-link" },
    { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts-link" },
    { to: "/engagements", label: "Engagements", icon: Briefcase, testid: "nav-engagements-link" },
    { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports-link" },
    { to: "/evaluations", label: "Evaluations", icon: ClipboardList, testid: "nav-evaluations-link" },
    { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices-link" },
    { to: "/directory", label: "School Directory", icon: Building2, testid: "nav-directory-link" },
    ...(isAdmin ? [{ to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings-link" }] : []),
  ];

  return (
    <div className="min-h-screen bg-canvas flex" data-testid="app-layout">
      <aside className="w-64 shrink-0 bg-navy text-white flex flex-col sticky top-0 h-screen" data-testid="sidebar">
        <div className="px-5 pt-6 pb-5 border-b border-white/10">
          <Wordmark light />
          <div className="mt-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white/50">
            <Lock size={11} /> Operations Hub
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" data-testid="sidebar-nav">
          {links.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-terracotta flex items-center justify-center font-black text-sm" data-testid="user-avatar">
              {user?.name?.[0] || "?"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[11px] text-white/50 flex items-center gap-1" data-testid="user-role">
                {isAdmin && <ShieldCheck size={11} />} {isAdmin ? "CKingEducation Admin" : "Consultant"}
              </div>
            </div>
            <button onClick={async () => { await logout(); navigate("/login"); }}
              className="text-white/50 hover:text-white transition-colors" title="Sign out" data-testid="logout-button">
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-navy text-white px-6 sm:px-8 lg:px-10 py-4 flex items-center justify-between sticky top-0 z-10 border-b border-white/10" data-testid="top-banner">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50 flex items-center gap-1">
            <Lock size={10} /> Operations Hub
          </span>
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-1.5 text-sm font-bold text-white/70 hover:text-white transition-colors px-3 py-1.5"
              data-testid="banner-hub-home-link">
              <Home size={15} /> Hub Home
            </Link>
            <span className="text-sm text-white/60 hidden md:inline" data-testid="banner-user-name">{user?.name}</span>
            <button className="text-white/60 hover:text-white transition-colors p-1.5" title="Sign out" data-testid="banner-logout-button"
              onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut size={16} />
            </button>
          </div>
        </header>
        <main className="flex-1 px-6 sm:px-8 lg:px-10 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
